import React, { useEffect, useId, useRef, useState } from "react";
import { CustomerCompany, searchCustomerCompanies } from "../services/api/platform.service";

export const CUSTOMER_SEARCH_MIN_CHARS = 2;
export const CUSTOMER_SEARCH_DEBOUNCE_MS = 300;
export const CUSTOMER_SEARCH_LIMIT = 20;

type SearchState = "idle" | "loading" | "done" | "error";

interface FindCustomerProps {
  language: any;
  onSelect: (companyId: string) => void;
}

const toRows = (raw: unknown): CustomerCompany[] =>
  (Array.isArray(raw) ? raw : [])
    .filter((row: any) => typeof row?.id === "string" && row.id !== "" && typeof row?.companyName === "string")
    .map((row: any) => ({
      id: row.id,
      companyName: row.companyName,
      status: typeof row.status === "string" ? row.status : null,
    }));

/** InnovX staff search for a customer company to open with their support role (combobox + listbox). */
const FindCustomer = ({ language, onSelect }: FindCustomerProps) => {
  const t = (key: string) => language?.[key] ?? key;
  const [query, setQuery] = useState("");
  const [state, setState] = useState<SearchState>("idle");
  const [rows, setRows] = useState<CustomerCompany[]>([]);
  const [count, setCount] = useState(0);
  const [active, setActive] = useState(-1);
  // Bumped by "Try again" so the same text is searched once more.
  const [attempt, setAttempt] = useState(0);
  const requestSeq = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Desktop sidebar and mobile drawer each mount a switcher, so ids must be unique per instance.
  const uid = useId();
  const ids = {
    input: `${uid}-input`,
    label: `${uid}-label`,
    hint: `${uid}-hint`,
    status: `${uid}-status`,
    list: `${uid}-list`,
    option: (index: number) => `${uid}-option-${index}`,
  };

  useEffect(() => {
    const term = query.trim();
    const seq = ++requestSeq.current;
    if (term.length < CUSTOMER_SEARCH_MIN_CHARS) {
      setState("idle");
      setRows([]);
      setCount(0);
      setActive(-1);
      return;
    }
    // Old results go while the new text is searched, so nobody picks a company from the previous query.
    setState("loading");
    setRows([]);
    setCount(0);
    setActive(-1);
    const timer = setTimeout(async () => {
      try {
        const { data } = await searchCustomerCompanies({ search: term, page: 1, limit: CUSTOMER_SEARCH_LIMIT });
        // A slower answer to an older query must not replace the newer one.
        if (seq !== requestSeq.current) return;
        const next = toRows(data?.data);
        setRows(next);
        setCount(typeof data?.count === "number" ? data.count : next.length);
        setActive(next.length > 0 ? 0 : -1);
        setState("done");
      } catch {
        if (seq !== requestSeq.current) return;
        setRows([]);
        setCount(0);
        setActive(-1);
        setState("error");
      }
    }, CUSTOMER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, attempt]);

  useEffect(
    () => () => {
      requestSeq.current += 1;
    },
    []
  );

  useEffect(() => {
    if (active < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const choose = (row: CustomerCompany | undefined) => {
    if (state === "done" && row) onSelect(row.id);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && rows.length > 0) {
      event.preventDefault();
      setActive((index) => Math.min(index + 1, rows.length - 1));
    } else if (event.key === "ArrowUp" && rows.length > 0) {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === "Home" && rows.length > 0) {
      event.preventDefault();
      setActive(0);
    } else if (event.key === "End" && rows.length > 0) {
      event.preventDefault();
      setActive(rows.length - 1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      // While a newer search runs, the rows on screen answer the previous text.
      if (state === "done" && active >= 0) choose(rows[active]);
    } else if (event.key === "Escape" && query) {
      event.preventDefault();
      event.stopPropagation();
      setQuery("");
    }
  };

  const term = query.trim();
  let status = "";
  if (state === "idle" && term.length > 0) status = t("Type at least 2 letters of the company name.");
  if (state === "loading") status = t("Searching…");
  if (state === "error") status = t("Customers could not be loaded. Try again.");
  if (state === "done" && rows.length === 0) status = t("No customers match your search.");
  if (state === "done" && count > rows.length) {
    status = t("{n} of {m} shown. Keep typing to narrow the list.")
      .replace("{n}", String(rows.length))
      .replace("{m}", String(count));
  }

  const expanded = state === "done" && rows.length > 0;

  return (
    <div className="space-y-[6px]">
      <label htmlFor={ids.input} id={ids.label} className="block text-xs text-[#05292A]">
        {t("Find a customer")}
      </label>
      <input
        id={ids.input}
        ref={inputRef}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        aria-controls={expanded ? ids.list : undefined}
        aria-activedescendant={expanded && active >= 0 ? ids.option(active) : undefined}
        aria-describedby={`${ids.hint} ${ids.status}`}
        autoComplete="off"
        spellCheck={false}
        maxLength={100}
        value={query}
        placeholder={t("Company Name")}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleKeyDown}
        className="w-full h-11 lg:h-9 px-3 bg-white border border-[#DBE4E4] rounded-xl text-[16px] lg:text-xs text-[#05292A] outline-none focus:border-[#0F7679]"
      />
      <p id={ids.hint} className="text-[12px] leading-4 text-[#6A7878]">
        {t("You enter with your support role. Each visit is logged.")}
      </p>
      <p
        id={ids.status}
        role="status"
        aria-live="polite"
        className={`text-[12px] leading-4 ${state === "error" ? "text-[#BA0E13]" : "text-[#6A7878]"}`}
      >
        {status}
      </p>
      {state === "error" && (
        <button
          type="button"
          onClick={() => {
            setAttempt((n) => n + 1);
            inputRef.current?.focus();
          }}
          className="h-11 lg:h-auto text-[12px] leading-4 font-medium text-[#0F7679] hover:underline"
        >
          {t("Try again")}
        </button>
      )}
      {expanded && (
        <ul
          id={ids.list}
          ref={listRef}
          role="listbox"
          aria-labelledby={ids.label}
          className="switch-list-scroll max-h-[min(40vh,286px)] bg-white border border-[#DBE4E4] rounded-xl p-1"
        >
          {rows.map((row, index) => (
            <li
              key={row.id}
              id={ids.option(index)}
              data-index={index}
              role="option"
              aria-selected={index === active}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(row)}
              className={`flex flex-col justify-center w-full min-h-[44px] lg:min-h-[36px] px-2 py-1 rounded-lg text-[13px] lg:text-xs text-left text-[#05292A] cursor-pointer ${
                index === active ? "bg-[#E7F1F1]" : ""
              }`}
            >
              <span className="truncate" title={row.companyName}>{row.companyName}</span>
              {/* Staff should see a frozen or overdue customer before they open it. */}
              {row.status && row.status !== "Active" && (
                <span className="text-[11px] leading-4 text-[#BA0E13]">{t(row.status)}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FindCustomer;
